/**
 * Lets the assembled Plan of Record instrument (same pages under Prioritization and Planning)
 * generate correct URLs for the shell it sits in.
 */
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import {
  PRIORITIZATION_PLAN_INSTRUMENT_PATH,
  planInstrumentHref,
} from '../lib/prioritizationPlanInstrumentPath';

type Ctx = {
  basePath: string;
  /**
   * When set, the instrument is embedded under one record's own base (the
   * plan-cycle narrative subpage, /planning/plans/:cycleId/narrative) and
   * porHref builds `${porBasePath}/<subpath>` — the porId is implied by the
   * mount, not the URL.
   */
  porBasePath?: string;
};

const PlanInstrumentNavContext = createContext<Ctx | null>(null);

export function PlanInstrumentNavProvider({
  basePath,
  porBasePath,
  children,
}: {
  basePath: string;
  porBasePath?: string;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ basePath, porBasePath }), [basePath, porBasePath]);
  return (
    <PlanInstrumentNavContext.Provider value={value}>{children}</PlanInstrumentNavContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook paired with provider
export function usePlanInstrumentNav() {
  const ctx = useContext(PlanInstrumentNavContext);
  const basePath = ctx?.basePath ?? PRIORITIZATION_PLAN_INSTRUMENT_PATH;
  const porBasePath = ctx?.porBasePath;

  const porHref = useCallback(
    (porId: string, subpath?: string) => {
      if (porBasePath) {
        const s = (subpath ?? '').replace(/^\//, '');
        const base = porBasePath.replace(/\/$/, '');
        return s ? `${base}/${s}` : base;
      }
      return planInstrumentHref(basePath, porId, subpath ?? '');
    },
    [basePath, porBasePath],
  );

  return {
    basePath,
    /** Index — smart-redirects to the workspace primary */
    instrumentListHref: basePath,
    /** Full catalog (set primary / browse all) */
    instrumentCatalogHref: `${basePath}?catalog=1`,
    instrumentNewHref: `${basePath}/new`,
    porHref,
  };
}
